/**
 * Bluedot Redux Reducer
 *
 * Manages Bluedot SDK state in Redux store.
 */

import {
  BLUEDOT_INITIALIZE,
  BLUEDOT_INITIALIZE_SUCCESS,
  BLUEDOT_INITIALIZE_FAILURE,
  BLUEDOT_LOCATION_ENABLE,
  BLUEDOT_LOCATION_ENABLED,
  BLUEDOT_LOCATION_FAILURE,
  BLUEDOT_WAVE_SEND,
  BLUEDOT_WAVE_SENT,
  BLUEDOT_WAVE_FAILURE,
  BLUEDOT_RESET,
  BLUEDOT_ERROR,
  BLUEDOT_NATIVE_PERMISSION_CHECK,
  BLUEDOT_NATIVE_PERMISSION_GRANTED,
  BLUEDOT_NATIVE_PERMISSION_DENIED,
  BLUEDOT_STATE_KEYS
} from "./constants";

// Initial state
const initialState = {
  [BLUEDOT_STATE_KEYS.ENABLED]: false,
  [BLUEDOT_STATE_KEYS.INITIALIZED]: false,
  [BLUEDOT_STATE_KEYS.INITIALIZING]: false,
  [BLUEDOT_STATE_KEYS.LOCATION_ENABLED]: false,
  [BLUEDOT_STATE_KEYS.LOCATION_REQUESTING]: false,
  [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_GRANTED]: false,
  [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_CHECKED]: false,
  [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_STATUS]: "unknown",
  [BLUEDOT_STATE_KEYS.WAVE_SENDING]: false,
  [BLUEDOT_STATE_KEYS.LAST_WAVE]: null,
  [BLUEDOT_STATE_KEYS.ERROR]: null
};

/**
 * Bluedot reducer
 *
 * @param {Object} state - Current state
 * @param {Object} action - Redux action
 * @returns {Object} - New state
 */
const bluedotReducer = (state = initialState, action) => {
  switch (action.type) {
    case BLUEDOT_INITIALIZE:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.INITIALIZING]: true,
        [BLUEDOT_STATE_KEYS.ERROR]: null
      };

    case BLUEDOT_INITIALIZE_SUCCESS:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.ENABLED]: true,
        [BLUEDOT_STATE_KEYS.INITIALIZED]: true,
        [BLUEDOT_STATE_KEYS.INITIALIZING]: false,
        [BLUEDOT_STATE_KEYS.ERROR]: null
      };

    case BLUEDOT_INITIALIZE_FAILURE:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.ENABLED]: false,
        [BLUEDOT_STATE_KEYS.INITIALIZED]: false,
        [BLUEDOT_STATE_KEYS.INITIALIZING]: false,
        [BLUEDOT_STATE_KEYS.ERROR]: action.payload
      };

    case BLUEDOT_LOCATION_ENABLE:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.LOCATION_REQUESTING]: true,
        [BLUEDOT_STATE_KEYS.ERROR]: null
      };

    case BLUEDOT_LOCATION_ENABLED:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.LOCATION_ENABLED]: true,
        [BLUEDOT_STATE_KEYS.LOCATION_REQUESTING]: false,
        [BLUEDOT_STATE_KEYS.ERROR]: null
      };

    case BLUEDOT_LOCATION_FAILURE:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.LOCATION_ENABLED]: false,
        [BLUEDOT_STATE_KEYS.LOCATION_REQUESTING]: false,
        [BLUEDOT_STATE_KEYS.ERROR]: action.payload
      };

    case BLUEDOT_WAVE_SEND:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.WAVE_SENDING]: true,
        [BLUEDOT_STATE_KEYS.ERROR]: null
      };

    case BLUEDOT_WAVE_SENT:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.WAVE_SENDING]: false,
        [BLUEDOT_STATE_KEYS.LAST_WAVE]: {
          timestamp: new Date().toISOString(),
          destinationId: action.payload.destinationId,
          success: true
        },
        [BLUEDOT_STATE_KEYS.ERROR]: null
      };

    case BLUEDOT_WAVE_FAILURE:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.WAVE_SENDING]: false,
        [BLUEDOT_STATE_KEYS.LAST_WAVE]: {
          timestamp: new Date().toISOString(),
          success: false,
          error: action.payload
        },
        [BLUEDOT_STATE_KEYS.ERROR]: action.payload
      };

    case BLUEDOT_NATIVE_PERMISSION_CHECK:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_CHECKED]: true,
        [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_STATUS]:
          action.payload?.status || "unknown"
      };

    case BLUEDOT_NATIVE_PERMISSION_GRANTED:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_GRANTED]: true,
        [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_CHECKED]: true,
        [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_STATUS]: "granted"
      };

    case BLUEDOT_NATIVE_PERMISSION_DENIED:
      return {
        ...state,
        [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_GRANTED]: false,
        [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_CHECKED]: true,
        [BLUEDOT_STATE_KEYS.NATIVE_PERMISSION_STATUS]: "denied"
      };

    case BLUEDOT_RESET:
    case BLUEDOT_ERROR:
      return {
        ...initialState,
        [BLUEDOT_STATE_KEYS.ERROR]: action.payload || null
      };

    default:
      return state;
  }
};

export default bluedotReducer;
